import { createServiceRoleClient } from '@/lib/supabase/service'
import type {
  AdminShopRow,
  AdminSubscriptionRequestRow,
  BillingSettingsRow,
  DashboardSummary,
  EffectiveEntitlementRow,
  MembershipRow,
  ProfileRow,
  ShopDetailPayload,
} from '@/lib/types'

const PAGE_SIZE_DEFAULT = 20

async function fetchProfilesByIds(userIds: string[]) {
  const uniqueIds = [...new Set(userIds.filter(Boolean))]
  if (uniqueIds.length === 0) {
    return new Map<string, ProfileRow>()
  }

  const supabase = createServiceRoleClient()
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, phone, created_at')
    .in('id', uniqueIds)

  if (error) {
    throw error
  }

  return new Map((data ?? []).map((row) => [row.id, row as ProfileRow]))
}

export async function getDashboardSummary(): Promise<DashboardSummary> {
  const supabase = createServiceRoleClient()

  const [pendingRes, approvedRes, activeRes, inactiveRes] = await Promise.all([
    supabase.from('subscription_requests').select('id', { head: true, count: 'exact' }).eq('status', 'pending'),
    supabase.from('subscription_requests').select('id', { head: true, count: 'exact' }).eq('status', 'approved'),
    supabase.from('v_shop_entitlements_effective').select('shop_id', { head: true, count: 'exact' }).eq('is_effective_active', true),
    supabase.from('v_shop_entitlements_effective').select('shop_id', { head: true, count: 'exact' }).eq('is_effective_active', false),
  ])

  for (const response of [pendingRes, approvedRes, activeRes, inactiveRes]) {
    if (response.error) throw response.error
  }

  return {
    pendingRequests: pendingRes.count ?? 0,
    approvedRequests: approvedRes.count ?? 0,
    activeShops: activeRes.count ?? 0,
    inactiveOrExpiredShops: inactiveRes.count ?? 0,
  }
}

export async function listSubscriptionRequests(params?: {
  status?: string
  search?: string
  page?: number
  pageSize?: number
}) {
  const supabase = createServiceRoleClient()
  const page = Math.max(params?.page ?? 1, 1)
  const pageSize = Math.min(Math.max(params?.pageSize ?? PAGE_SIZE_DEFAULT, 1), 100)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  let query = supabase
    .from('v_admin_subscription_requests')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to)

  if (params?.status && params.status !== 'all') {
    query = query.eq('status', params.status)
  }

  if (params?.search) {
    const q = params.search.trim()
    if (q) {
      query = query.or(`shop_name.ilike.%${q}%,payer_phone.ilike.%${q}%,note.ilike.%${q}%`)
    }
  }

  const { data, count, error } = await query

  if (error) {
    throw error
  }

  const rows = (data ?? []) as AdminSubscriptionRequestRow[]
  const profiles = await fetchProfilesByIds(rows.flatMap((row) => [row.user_id, row.decided_by ?? '']))

  return {
    rows: rows.map((row) => ({
      ...row,
      requesterProfile: profiles.get(row.user_id) ?? null,
      decidedByProfile: row.decided_by ? profiles.get(row.decided_by) ?? null : null,
    })),
    count: count ?? 0,
    page,
    pageSize,
  }
}

export async function listShops(params?: {
  search?: string
  plan?: string
  page?: number
  pageSize?: number
}) {
  const supabase = createServiceRoleClient()
  const page = Math.max(params?.page ?? 1, 1)
  const pageSize = Math.min(Math.max(params?.pageSize ?? PAGE_SIZE_DEFAULT, 1), 100)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  let query = supabase
    .from('v_admin_shops')
    .select('*', { count: 'exact' })
    .order('entitlement_updated_at', { ascending: false, nullsFirst: false })
    .range(from, to)

  if (params?.plan && params.plan !== 'all') {
    query = query.eq('plan', params.plan)
  }

  if (params?.search) {
    const q = params.search.trim()
    if (q) {
      query = query.or(`shop_name.ilike.%${q}%,shop_phone.ilike.%${q}%`)
    }
  }

  const { data, count, error } = await query

  if (error) {
    throw error
  }

  return {
    rows: (data ?? []) as AdminShopRow[],
    count: count ?? 0,
    page,
    pageSize,
  }
}

export async function getShopDetail(shopId: string): Promise<ShopDetailPayload> {
  const supabase = createServiceRoleClient()

  const [shopRes, effectiveRes, billingRes, membershipsRes, requestsRes] = await Promise.all([
    supabase.from('v_admin_shops').select('*').eq('shop_id', shopId).maybeSingle(),
    supabase.from('v_shop_entitlements_effective').select('*').eq('shop_id', shopId).maybeSingle(),
    supabase.from('shop_billing_settings').select('*').eq('shop_id', shopId).maybeSingle(),
    supabase.from('memberships').select('shop_id, user_id, role, created_at').eq('shop_id', shopId).order('created_at', { ascending: true }),
    supabase.from('v_admin_subscription_requests').select('*').eq('shop_id', shopId).order('created_at', { ascending: false }).limit(20),
  ])

  for (const response of [shopRes, effectiveRes, billingRes, membershipsRes, requestsRes]) {
    if (response.error) throw response.error
  }

  const membershipRows = (membershipsRes.data ?? []) as MembershipRow[]
  const requestRows = (requestsRes.data ?? []) as AdminSubscriptionRequestRow[]
  const profileIds = [
    ...membershipRows.map((row) => row.user_id),
    ...requestRows.map((row) => row.user_id),
    ...requestRows.map((row) => row.decided_by ?? ''),
  ]
  const profiles = await fetchProfilesByIds(profileIds)

  return {
    shop: (shopRes.data ?? null) as AdminShopRow | null,
    effectiveEntitlement: (effectiveRes.data ?? null) as EffectiveEntitlementRow | null,
    billingSettings: (billingRes.data ?? null) as BillingSettingsRow | null,
    memberships: membershipRows.map((row) => ({
      ...row,
      profile: profiles.get(row.user_id) ?? null,
    })),
    recentRequests: requestRows.map((row) => ({
      ...row,
      requesterProfile: profiles.get(row.user_id) ?? null,
      decidedByProfile: row.decided_by ? profiles.get(row.decided_by) ?? null : null,
    })),
  }
}
